import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Vote, CheckCircle2, BarChart3, User, AlertCircle, Clock, Star, MessageSquare, ChevronRight, Loader2, Lock, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const TYPE_LABELS = {
  election_bureau: 'Élection Bureau National',
  election_regionale: 'Élection Régionale',
  resolution_ag: 'Résolution d\'AG',
  sondage: 'Sondage',
  election: 'Élection',
};

const TYPE_COLORS = {
  election_bureau: '#1e3a5f',
  election_regionale: '#7c3aed',
  resolution_ag: '#d97706',
  sondage: '#059669',
  election: '#35AFE4',
};

const LIFECYCLE_STEPS = ['Brouillon', 'Candidatures', 'Vote ouvert', 'Clôturé', 'Publié'];

const StatusBadge = ({ status }) => {
  const map = {
    draft: { label: 'Brouillon', color: '#64748b', bg: '#f1f5f9' },
    candidatures: { label: 'Candidatures', color: '#7c3aed', bg: '#f5f3ff' },
    active: { label: 'Vote ouvert', color: '#059669', bg: '#ecfdf5' },
    closed: { label: 'Clôturé', color: '#d97706', bg: '#fffbeb' },
    published: { label: 'Publié', color: '#1e3a5f', bg: '#eaf6fc' },
  };
  const s = map[status] || map.draft;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: s.color, backgroundColor: s.bg }}>{s.label}</span>;
};

const LifecycleFrise = ({ step = 0, status }) => {
  const stepIndex = ['draft', 'candidatures', 'active', 'closed', 'published'].indexOf(status);
  const active = stepIndex >= 0 ? stepIndex : step;
  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-2 my-3" data-testid="lifecycle-frise">
      {LIFECYCLE_STEPS.map((label, i) => (
        <div key={i} className="flex items-center gap-1 flex-shrink-0">
          <div className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-[9px] font-bold ${
            i <= active ? 'bg-[#059669] text-white' : 'bg-gray-200 text-gray-400'
          }`}>
            {i < active ? '✓' : i + 1}
          </div>
          <span className={`text-[9px] whitespace-nowrap ${i <= active ? 'font-bold text-gray-900' : 'text-gray-400'}`}>{label}</span>
          {i < LIFECYCLE_STEPS.length - 1 && <div className={`w-3 sm:w-6 h-px ${i < active ? 'bg-[#059669]' : 'bg-gray-200'}`} />}
        </div>
      ))}
    </div>
  );
};

const CandidatureForm = ({ election, headers, onDone }) => {
  const [foi, setFoi] = useState('');
  const [video, setVideo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!foi.trim()) { toast.error('La profession de foi est requise'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/elections/${election.id}/candidature`, {
        profession_de_foi: foi.trim(),
        video_url: video.trim(),
      }, { headers });
      toast.success('Candidature déposée !');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  return (
    <div className="mb-4 border border-purple-200 bg-purple-50/50 rounded-xl p-4" data-testid="candidature-form">
      <h3 className="text-sm font-bold text-[#7c3aed] mb-3">Déposer votre candidature</h3>
      <div className="mb-3">
        <label className="text-xs font-medium text-gray-700 mb-1 block">Profession de foi *</label>
        <textarea value={foi} onChange={e => setFoi(e.target.value)} rows={4}
          placeholder="Présentez votre programme et vos motivations..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/30 resize-none"
          data-testid="candidature-foi" />
      </div>
      <div className="mb-3">
        <label className="text-xs font-medium text-gray-700 mb-1 block">URL vidéo (optionnel)</label>
        <input value={video} onChange={e => setVideo(e.target.value)}
          placeholder="https://youtube.com/..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/30"
          data-testid="candidature-video" />
      </div>
      <button onClick={submit} disabled={!foi.trim() || submitting}
        className="px-5 py-2 bg-[#7c3aed] text-white rounded-lg text-sm font-bold hover:bg-[#6d28d9] disabled:opacity-50 transition-colors"
        data-testid="submit-candidature-btn">
        {submitting ? <Loader2 size={14} className="animate-spin inline mr-1" /> : null}Déposer ma candidature
      </button>
    </div>
  );
};

export default function MemberVotesPage() {
  const navigate = useNavigate();
  const [activeVotes, setActiveVotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeElection, setActiveElection] = useState(null);
  const [selected, setSelected] = useState([]);
  const [pollAnswers, setPollAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { loadVotes(); }, []);

  const loadVotes = async () => {
    try {
      const [activeRes, histRes] = await Promise.all([
        axios.get(`${API}/votes/active`, { headers }),
        axios.get(`${API}/votes/history`, { headers }),
      ]);
      setActiveVotes(activeRes.data);
      setHistory(histRes.data);
    } catch { }
    setLoading(false);
  };

  const openElection = async (e) => {
    setActiveElection(e);
    setSelected([]);
    setPollAnswers({});
    setResults(null);
    if (e.has_voted) {
      try {
        const endpoint = e.type === 'sondage' ? `${API}/elections/${e.id}/poll-results` : `${API}/elections/${e.id}/results`;
        const r = await axios.get(endpoint, { headers });
        setResults(r.data);
      } catch { }
    }
  };

  const castVote = async () => {
    if (!activeElection) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/elections/${activeElection.id}/vote`, { choices: selected }, { headers });
      toast.success('Vote enregistré !');
      loadVotes();
      setActiveElection(prev => ({ ...prev, has_voted: true }));
      try {
        const r = await axios.get(`${API}/elections/${activeElection.id}/results`, { headers });
        setResults(r.data);
      } catch { }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const castBlank = async () => {
    if (!activeElection) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/elections/${activeElection.id}/vote-blanc`, {}, { headers });
      toast.success('Vote blanc enregistré');
      loadVotes();
      setActiveElection(prev => ({ ...prev, has_voted: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const submitPoll = async () => {
    if (!activeElection) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/elections/${activeElection.id}/poll-vote`, { answers: pollAnswers }, { headers });
      toast.success('Réponses enregistrées !');
      loadVotes();
      setActiveElection(prev => ({ ...prev, has_voted: true }));
      try {
        const r = await axios.get(`${API}/elections/${activeElection.id}/poll-results`, { headers });
        setResults(r.data);
      } catch { }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const toggleCandidate = (id) => {
    if (activeElection?.has_voted) return;
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= (activeElection?.max_choices || 1)) {
        toast.error(`Maximum ${activeElection.max_choices} choix`);
        return prev;
      }
      return [...prev, id];
    });
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-gray-400" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="member-votes-page">
      <h1 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2"><Vote size={20} className="text-[#35AFE4]" /> Votes & Sondages</h1>

      {/* Active votes */}
      {activeVotes.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">En cours</h2>
          <div className="grid gap-3">
            {activeVotes.map(e => {
              const color = TYPE_COLORS[e.type] || '#35AFE4';
              return (
                <button key={e.id} onClick={() => openElection(e)}
                  className={`bg-white rounded-xl border-2 p-4 text-left transition-all hover:shadow-md ${activeElection?.id === e.id ? 'ring-2' : ''}`}
                  style={activeElection?.id === e.id ? { borderColor: color, boxShadow: `0 0 0 2px ${color}20` } : { borderColor: '#e5e7eb' }}
                  data-testid={`vote-card-${e.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{TYPE_LABELS[e.type] || e.type}</span>
                    <StatusBadge status={e.status} />
                  </div>
                  <p className="text-sm font-bold text-gray-900">{e.title}</p>
                  {e.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{e.description}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-3 text-[10px] text-gray-400">
                      {e.end_date && <span><Clock size={10} className="inline mr-0.5" /> Fin : {new Date(e.end_date).toLocaleDateString('fr-FR')}</span>}
                      {e.candidates?.length > 0 && <span><User size={10} className="inline mr-0.5" /> {e.candidates.length} candidats</span>}
                    </div>
                    {e.has_voted ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600"><CheckCircle2 size={12} /> Voté</span>
                    ) : e.status === 'candidatures' && e.accept_candidatures ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-[#7c3aed]">Candidater <ChevronRight size={12} /></span>
                    ) : e.type === 'sondage' ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-[#059669]">Répondre <ChevronRight size={12} /></span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-[#35AFE4]">Voter <ChevronRight size={12} /></span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Active election detail */}
      {activeElection && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6" data-testid="vote-detail">
          <LifecycleFrise status={activeElection.status} step={activeElection.lifecycle_step} />
          <h2 className="text-lg font-bold text-gray-900 mb-2">{activeElection.title}</h2>
          {activeElection.description && <p className="text-sm text-gray-500 mb-4">{activeElection.description}</p>}

          {/* Candidature form */}
          {activeElection.status === 'candidatures' && activeElection.accept_candidatures && !activeElection.my_candidature ? (
            <CandidatureForm election={activeElection} headers={headers} onDone={() => { loadVotes(); setActiveElection(prev => ({ ...prev, my_candidature: 'pending' })); }} />
          ) : activeElection.my_candidature ? (
            <div className="mb-4 p-3 rounded-xl bg-gray-50 text-sm">
              <p className="font-bold text-gray-700">Candidature déposée</p>
              <p className="text-xs text-gray-500">Statut : <span className={activeElection.my_candidature === 'approved' ? 'text-emerald-600 font-bold' : activeElection.my_candidature === 'rejected' ? 'text-red-600 font-bold' : 'text-amber-600 font-bold'}>{activeElection.my_candidature === 'approved' ? 'Approuvée' : activeElection.my_candidature === 'rejected' ? 'Refusée' : 'En attente'}</span></p>
            </div>
          ) : null}

          {/* Sondage questions */}
          {activeElection.type === 'sondage' && activeElection.questions?.length > 0 && !activeElection.has_voted && activeElection.status === 'active' ? (
            <div className="space-y-4 mb-4">
              {activeElection.questions.map((q, qi) => (
                <div key={q.id} className="border border-gray-100 rounded-xl p-4" data-testid={`poll-question-${qi}`}>
                  <p className="text-sm font-bold text-gray-900 mb-2">{q.text}</p>
                  {q.type === 'single' || q.type === 'pour_contre_abstention' ? (
                    <div className="space-y-1.5">
                      {(q.type === 'pour_contre_abstention' ? ['Pour', 'Contre', 'Abstention'] : q.options).map((opt, oi) => (
                        <label key={oi} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">
                          <input type="radio" name={`q-${q.id}`} value={opt} checked={pollAnswers[q.id] === opt}
                            onChange={() => setPollAnswers(prev => ({ ...prev, [q.id]: opt }))} className="accent-[#059669]" />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'multiple' ? (
                    <div className="space-y-1.5">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">
                          <input type="checkbox" checked={(pollAnswers[q.id] || []).includes(opt)}
                            onChange={() => {
                              setPollAnswers(prev => {
                                const curr = prev[q.id] || [];
                                return { ...prev, [q.id]: curr.includes(opt) ? curr.filter(x => x !== opt) : [...curr, opt] };
                              });
                            }} className="accent-[#059669]" />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : q.type === 'stars' ? (
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(s => (
                        <button key={s} onClick={() => setPollAnswers(prev => ({ ...prev, [q.id]: s }))}
                          className={`p-1 ${(pollAnswers[q.id] || 0) >= s ? 'text-amber-400' : 'text-gray-300'}`}>
                          <Star size={24} fill={(pollAnswers[q.id] || 0) >= s ? 'currentColor' : 'none'} />
                        </button>
                      ))}
                    </div>
                  ) : q.type === 'free_text' ? (
                    <textarea value={pollAnswers[q.id] || ''} onChange={e => setPollAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                      rows={3} placeholder="Votre réponse..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]/30" />
                  ) : null}
                </div>
              ))}
              <button onClick={submitPoll} disabled={submitting}
                className="px-6 py-2.5 bg-[#059669] text-white rounded-xl font-bold text-sm hover:bg-[#047857] disabled:opacity-50 transition-colors"
                data-testid="submit-poll-btn">
                {submitting ? <Loader2 size={16} className="animate-spin inline mr-2" /> : null}Envoyer mes réponses
              </button>
            </div>
          ) : activeElection.type !== 'sondage' && !activeElection.has_voted && activeElection.status === 'active' ? (
            /* Election candidates */
            <div className="mb-4">
              <div className="grid gap-2 mb-4">
                {activeElection.candidates?.map(c => (
                  <button key={c.id} onClick={() => toggleCandidate(c.id)}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                      selected.includes(c.id) ? 'border-[#059669] bg-[#059669]/5' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`candidate-${c.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      selected.includes(c.id) ? 'bg-[#059669] text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {selected.includes(c.id) ? <CheckCircle2 size={20} /> : c.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{c.name}</p>
                      {c.description && <p className="text-xs text-gray-500 line-clamp-1">{c.description}</p>}
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={castVote} disabled={selected.length === 0 || submitting}
                  className="px-6 py-2.5 bg-[#1e3a5f] text-white rounded-xl font-bold text-sm hover:bg-[#162d4a] disabled:opacity-50 transition-colors"
                  data-testid="cast-vote-btn">
                  {submitting ? <Loader2 size={16} className="animate-spin inline mr-2" /> : null}Voter
                </button>
                {activeElection.allow_blank_vote && (
                  <button onClick={castBlank} disabled={submitting}
                    className="px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                    data-testid="blank-vote-btn">
                    Vote blanc
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* Results */}
          {results && (
            <div className="mt-4 border-t border-gray-100 pt-4" data-testid="vote-results">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><BarChart3 size={16} /> Résultats</h3>
              {Array.isArray(results.results) ? (
                /* Election/Resolution results */
                results.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 mb-2">
                    <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{r.name}</span>
                        <span className="text-sm font-bold text-gray-900">{r.votes} voix</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{
                          width: `${results.total_voters > 0 ? (r.votes / results.total_voters) * 100 : 0}%`,
                          backgroundColor: i === 0 ? '#059669' : '#35AFE4'
                        }} />
                      </div>
                    </div>
                  </div>
                ))
              ) : results.results && typeof results.results === 'object' ? (
                /* Poll results */
                Object.entries(results.results).map(([qid, agg]) => (
                  <div key={qid} className="mb-4 border border-gray-100 rounded-xl p-3">
                    <p className="text-sm font-bold text-gray-900 mb-2">{agg.question}</p>
                    {agg.type === 'stars' ? (
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1,2,3,4,5].map(s => <Star key={s} size={18} className={agg.data?.average >= s ? 'text-amber-400' : 'text-gray-300'} fill={agg.data?.average >= s ? 'currentColor' : 'none'} />)}
                        </div>
                        <span className="text-sm font-bold text-gray-900">{agg.data?.average || 0}/5</span>
                        <span className="text-xs text-gray-400">({agg.total_responses} réponses)</span>
                      </div>
                    ) : agg.type === 'free_text' ? (
                      <div className="space-y-1">
                        {(agg.data?.responses || []).slice(0, 5).map((r, i) => (
                          <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-1.5">"{r}"</p>
                        ))}
                        {agg.total_responses > 5 && <p className="text-xs text-gray-400">+{agg.total_responses - 5} autres réponses</p>}
                      </div>
                    ) : (
                      Object.entries(agg.data || {}).map(([opt, count]) => (
                        <div key={opt} className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-600 w-32 truncate">{opt}</span>
                          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#35AFE4] rounded-full" style={{ width: `${agg.total_responses > 0 ? (count / agg.total_responses) * 100 : 0}%` }} />
                          </div>
                          <span className="text-xs font-bold text-gray-700">{count}</span>
                        </div>
                      ))
                    )}
                  </div>
                ))
              ) : null}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Historique</h2>
          <div className="space-y-2">
            {history.map(e => (
              <button key={e.id} onClick={() => openElection(e)}
                className="w-full bg-white rounded-xl border border-gray-200 p-3 text-left hover:bg-gray-50 transition-colors"
                data-testid={`history-${e.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{e.title}</p>
                    <p className="text-[10px] text-gray-400">{TYPE_LABELS[e.type] || e.type} · {e.closed_at ? new Date(e.closed_at).toLocaleDateString('fr-FR') : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={e.status} />
                    {e.has_voted && <CheckCircle2 size={14} className="text-emerald-500" />}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeVotes.length === 0 && history.length === 0 && (
        <div className="text-center py-16">
          <Vote size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aucun vote en cours</p>
        </div>
      )}
    </div>
  );
}
